import React, { useMemo, useState } from "react";
import { useStaffAllDocuments } from "../hooks/useStaffAllDocuments";
import { updateDocumentStatus, getDocumentDownloadUrl } from "../services/firestoreService";
import SignaturePortal from "../components/SignaturePortal";
import { TaxDocument } from "../types";
import { useAuth } from "../context/AuthContext";

const safeLower = (v: any) => (typeof v === "string" ? v.toLowerCase() : "");

const toDateSafe = (v: any): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return v;

  if (typeof v === "object" && typeof v.toDate === "function") {
    try {
      return v.toDate();
    } catch {
      return null;
    }
  }

  if (typeof v === "number") {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }

  if (typeof v === "string") {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }

  return null;
};

const formatDateTime = (v: any) => {
  const d = toDateSafe(v);
  if (!d) return "Unknown";
  try {
    return d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return d.toISOString();
  }
};

const allowedStatuses = new Set(["PENDING", "REJECTED", "ISSUE", "APPROVED", "ADDED_TO_RETURN"]);

const normalizeStatus = (s: any) => {
  const v = (typeof s === "string" ? s : "").trim().toUpperCase();
  return allowedStatuses.has(v) ? v : "PENDING";
};

const StaffDocumentsPage: React.FC = () => {
  const hook = useStaffAllDocuments();
  const documentsRaw: any = (hook as any)?.documents;
  const loading: boolean = !!(hook as any)?.loading;
  const hookError: any = (hook as any)?.error || null;

  const { firmId, firmSettings } = useAuth();

  const [searchTerm, setSearchTerm] = useState("");
  const [yearFilter, setYearFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [fetchingId, setFetchingId] = useState<string | null>(null);
  const [activeSignDoc, setActiveSignDoc] = useState<TaxDocument | null>(null);

  const brandColor = firmSettings.brandColor || "#a40000";

  const documents: TaxDocument[] = useMemo(() => {
    if (!Array.isArray(documentsRaw)) return [];
    return documentsRaw
      .filter(Boolean)
      .map((d: any) => ({
        ...d,
        status: normalizeStatus(d.status),
        clientName: typeof d.clientName === "string" ? d.clientName : "Unknown Client",
        fileName: typeof d.fileName === "string" ? d.fileName : "Untitled",
        storagePath: typeof d.storagePath === "string" ? d.storagePath : "",
        caseId: typeof d.caseId === "string" ? d.caseId : "",
        year: typeof d.year === "number" ? d.year : 0,
      }));
  }, [documentsRaw]);

  const handleStatusChange = async (caseId: string, docId: string, newStatus: any) => {
    if (!firmId) return;
    if (!caseId || !docId) return;
    await updateDocumentStatus(firmId, caseId, docId, newStatus);
  };

  const handleDownload = async (docId: string, storagePath: string) => {
    if (!storagePath) return;
    setFetchingId(docId);
    try {
      const url = await getDocumentDownloadUrl(storagePath);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error(e);
    } finally {
      setFetchingId(null);
    }
  };

  const filteredDocs = useMemo(() => {
    const q = safeLower(searchTerm.trim());
    return documents.filter((doc: any) => {
      const cn = safeLower(doc.clientName);
      const fn = safeLower(doc.fileName);
      const matchesSearch = !q || cn.includes(q) || fn.includes(q);
      const y = typeof doc.year === "number" ? doc.year : 0;
      const matchesYear = yearFilter === "all" || String(y) === yearFilter;
      const st = normalizeStatus(doc.status);
      const matchesStatus = statusFilter === "all" || st === statusFilter;
      return matchesSearch && matchesYear && matchesStatus;
    });
  }, [documents, searchTerm, yearFilter, statusFilter]);

  const years = useMemo(() => {
    const set = new Set<number>();
    documents.forEach((d: any) => {
      if (typeof d.year === "number" && d.year > 0) set.add(d.year);
    });
    return Array.from(set).sort((a, b) => b - a);
  }, [documents]);

  if (hookError) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-white rounded-2xl border border-red-100 shadow-sm p-8 text-center">
          <h2 className="text-xl font-black text-slate-900">Connection Interrupted</h2>
          <p className="text-slate-500 mt-2 text-sm">We encountered an error retrieving the document queue.</p>
          <button onClick={() => window.location.reload()} className="mt-6 px-6 py-3 rounded-xl bg-slate-900 text-white font-bold">Retry Connection</button>
        </div>
      </div>
    );
  }

  // The guard "if (loading) return <Spinner />" has been removed to ensure the page renders immediately.
  return (
    <div className="max-w-full mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Document Queue</h1>
          <p className="text-sm text-slate-500 font-medium">
            {loading ? 'Accessing secure vault...' : `Managing ${documents.length} active items.`}
          </p>
        </div>
      </div>

      {/* Filters Toolbar - Always visible */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col lg:flex-row gap-4 items-center">
        <div className="relative flex-1 w-full">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
          <input
            type="text"
            placeholder="Search by client or filename..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all font-medium"
          />
        </div>

        <div className="flex gap-2 w-full lg:w-auto">
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            className="flex-1 lg:w-32 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none font-bold text-slate-700"
          >
            <option value="all">All Years</option>
            {years.map((y) => (
              <option key={y} value={String(y)}>
                {y > 9000 ? "ID Folder" : y}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="flex-1 lg:w-48 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none font-bold text-slate-700"
          >
            <option value="all">All Pending Items</option>
            <option value="PENDING">Pending Review</option>
            <option value="REJECTED">Rejected</option>
            <option value="ISSUE">Needs Signature</option>
          </select>
        </div>
      </div>

      {/* Conditional Content Area */}
      {!loading && documents.length === 0 ? (
        <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm p-20 text-center animate-fade-in-up">
           <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-8 text-5xl grayscale opacity-30">📂</div>
           <h2 className="text-3xl font-black text-slate-800 tracking-tight">No Firm Documents Yet</h2>
           <p className="text-slate-500 mt-3 max-w-sm mx-auto text-base leading-relaxed">
             This firm's document vault is currently empty. Once clients upload files to their tax cases, they will appear here for review.
           </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden relative">
          {loading && (
             <div className="absolute top-0 left-0 w-full h-1 bg-brand-500/10 overflow-hidden">
                <div className="h-full bg-brand-500 animate-[loading-bar_1.5s_infinite]"></div>
             </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50/50 border-b border-slate-100">
                <tr>
                  <th className="px-6 py-4 font-semibold text-[11px] uppercase text-slate-400 tracking-widest">Client / File</th>
                  <th className="px-6 py-4 font-semibold text-[11px] uppercase text-slate-400 tracking-widest">Year / Type</th>
                  <th className="px-6 py-4 font-semibold text-[11px] uppercase text-slate-400 tracking-widest">Update Status</th>
                  <th className="px-6 py-4 font-semibold text-[11px] uppercase text-slate-400 tracking-widest">Last Updated</th>
                  <th className="px-6 py-4 font-semibold text-[11px] uppercase text-slate-400 tracking-widest text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-50">
                {filteredDocs.length === 0 && !loading && documents.length > 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-16 text-center">
                      <p className="text-slate-400 font-medium italic">No pending documents match your filters.</p>
                    </td>
                  </tr>
                ) : loading && documents.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-24 text-center">
                       <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-300 animate-pulse">Syncing Secure Vault...</p>
                    </td>
                  </tr>
                ) : (
                  filteredDocs.map((doc: any) => (
                    <tr key={doc.id} className="hover:bg-slate-50 transition-colors group">
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl group-hover:scale-110 transition-transform">📄</span>
                          <div>
                            <p className="font-bold text-slate-800 text-[14px]">{doc.clientName}</p>
                            <p className="text-[11px] text-slate-500 font-medium truncate max-w-[240px] mt-0.5">{doc.fileName}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <p className="font-bold text-slate-700 text-[13px]">{doc.year > 9000 ? "ID Folder" : doc.year || "Unknown"}</p>
                        <p className="text-[10px] font-bold uppercase text-brand-600 tracking-widest mt-0.5">{doc.filingType || "Document"}</p>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex flex-col gap-1.5 w-fit">
                          <StatusBadge status={doc.status} />
                          <select
                            value={doc.status}
                            onChange={(e) => handleStatusChange(doc.caseId, doc.id, e.target.value)}
                            className="px-2 py-1 text-[10px] border border-slate-200 rounded-lg font-bold text-slate-600 outline-none focus:ring-1 focus:ring-brand-500 bg-white shadow-sm"
                          >
                            <option value="PENDING">Pending</option>
                            <option value="REJECTED">Rejected</option>
                            <option value="ISSUE">Sig Required</option>
                            <option disabled>──────────</option>
                            <option value="APPROVED" className="text-emerald-600 font-bold">Approve</option>
                            <option value="ADDED_TO_RETURN" className="text-emerald-600 font-bold">Process</option>
                          </select>
                        </div>
                      </td>
                      <td className="px-6 py-5 text-[12px] font-medium text-slate-500 tabular-nums">{formatDateTime(doc.uploadedAt)}</td>
                      <td className="px-6 py-5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => handleDownload(doc.id, doc.storagePath)} disabled={fetchingId === doc.id} className="p-2.5 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-all text-slate-600 flex items-center justify-center shadow-sm">
                            {fetchingId === doc.id ? <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div> : "👁️"}
                          </button>
                          <button onClick={() => setActiveSignDoc(doc)} className="p-2.5 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100 rounded-xl transition-all text-indigo-600 flex items-center justify-center shadow-sm">✍️</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeSignDoc && (
        <SignaturePortal
          caseId={(activeSignDoc as any).caseId}
          document={activeSignDoc}
          isStaff={true}
          onClose={() => setActiveSignDoc(null)}
        />
      )}
    </div>
  );
};

const StatusBadge = ({ status }: { status: string }) => {
  const s = normalizeStatus(status);
  let styles = "bg-slate-100 text-slate-600 border-slate-200";
  if (s === "REJECTED") styles = "bg-red-50 text-red-700 border-red-100";
  if (s === "PENDING") styles = "bg-brand-50 text-brand-700 border-brand-100 ring-1 ring-brand-100";
  if (s === "ISSUE") styles = "bg-indigo-50 text-indigo-700 border-indigo-100";

  return (
    <span className={`px-2 py-0.5 text-[9px] font-bold rounded-lg uppercase tracking-widest border ${styles}`}>
      {s === "ISSUE" ? "Sig Required" : s.replace(/_/g, " ")}
    </span>
  );
};

export default StaffDocumentsPage;
